import { Fragment, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { useThemeColors } from '../hooks/useThemeColors';
import { formatChartDay } from '@/lib/datetime';
import { Select, type SelectOption } from '@/components/Select';
import { COUNTRY_MAP } from '@/lib/countries';
import {
  getPerfRows, getPerfCampaigns, getPerfSeries, getNoAppPerfRows,
  type PerfRow, type PerfVerdict, type PerfThresholds, type FbHealth, type FbHealthReason,
} from '@/api/perf';
import { explainRow, type PerfExplanation } from '@/lib/perf-explain';
import {
  groupAppRows, groupNoAppRows, sumGroups,
  type PerfGroup, type GeoLine, type PerfTotals,
} from '@/lib/perf-groups';

const DAY_MS = 24 * 60 * 60 * 1000;

function dateStr(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}
function fmtCents(c: number): string {
  return `$${(c / 100).toFixed(2)}`;
}
function fmtPct(v: number | null, digits = 1): string {
  return v == null ? '—' : `${(v * 100).toFixed(digits)}%`;
}
function fmtNum(v: number | null): string {
  return v == null ? '—' : v.toLocaleString('en-US');
}
function countryLabel(code: string): string {
  if (code === '??') return 'Unknown';
  return COUNTRY_MAP[code]?.name || code;
}

const VERDICTS: Record<PerfVerdict, { icon: string; label: string; hint: string }> = {
  crash: { icon: '🔥', label: 'Crash', hint: 'Crash-free rate below threshold — likely an app crash' },
  delivery: { icon: '📉', label: 'Delivery', hint: 'Registrations dropped and arrivals are far below clicks — traffic is not reaching the app: Play gate, redirect or geo block' },
  offer: { icon: '🎯', label: 'Offer', hint: 'Users arrive but stop registering — offer link / webview bug' },
  dep: { icon: '💸', label: 'Deposits', hint: 'Registrations hold but deposits collapsed' },
  geo_lag: { icon: '🌍', label: 'Geo lag', hint: 'Stable, but structurally behind the app’s other geos' },
  none: { icon: '', label: '', hint: '' },
};

/** Δ against the same-length preceding period, as a signed % of the baseline. */
function DeltaBadge({ value, base, muted }: { value: number | null; base: number | null; muted?: boolean }) {
  if (muted || value == null || base == null || base <= 0) return null;
  const pct = (value / base - 1) * 100;
  if (!Number.isFinite(pct)) return null;
  const up = pct >= 0;
  const cls = Math.abs(pct) < 10
    ? 'text-content-subtle'
    : up ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400';
  return (
    <span className={`ml-1 text-xs ${cls}`} title="vs the preceding period of the same length">
      {up ? '▲' : '▼'}{Math.abs(pct).toFixed(0)}%
    </span>
  );
}

const FB_REASONS: Record<FbHealthReason, string> = {
  auth_error: 'Firebase key rejected',
  no_link: 'no Firebase account',
  stale: 'country data not synced yet',
};

/**
 * An empty arrivals column reads exactly like "nobody reached the app", so a
 * broken Firebase link would quietly masquerade as a delivery problem. This
 * names the apps where the column is missing rather than zero.
 */
function FbHealthNotice({ health }: { health: FbHealth }) {
  const [open, setOpen] = useState(false);
  if (health.app_count === 0) return null;
  const pct = health.total_clicks > 0 ? Math.round((health.clicks / health.total_clicks) * 100) : 0;

  return (
    <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-900 dark:bg-amber-950/40">
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-center gap-2 text-left text-amber-800 dark:text-amber-200">
        <span>⚠️</span>
        <span>
          No Firebase arrivals for <span className="font-medium">{health.app_count} app(s)</span>
          {health.clicks > 0 && <> — {fmtNum(health.clicks)} clicks ({pct}% of the period) have no “New users” to compare against</>}
        </span>
        <span className="ml-auto text-xs underline">{open ? 'hide' : 'details'}</span>
      </button>
      {open && (
        <ul className="mt-2 space-y-1 text-xs text-amber-900 dark:text-amber-100/90">
          {health.apps.map((a) => (
            <li key={a.appId} className="flex flex-wrap gap-x-2">
              <span className="font-medium">{a.appName}</span>
              <span>· {FB_REASONS[a.reason]}</span>
              {a.lastCountrySync && <span>· last sync {a.lastCountrySync}</span>}
              {a.clicks > 0 && <span>· {fmtNum(a.clicks)} clicks</span>}
              {a.error && <span className="opacity-80">· {a.error}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** One number of the funnel, in the order money actually travels it. */
function Kpi({ label, value, sub, delta }: {
  label: string; value: string; sub?: string; delta?: { value: number | null; base: number | null };
}) {
  return (
    <div className="min-w-[7.5rem] px-3 py-2">
      <div className="text-xs uppercase tracking-wide text-content-subtle">{label}</div>
      <div className="text-lg font-semibold text-content">
        {value}
        {delta && <DeltaBadge value={delta.value} base={delta.base} />}
      </div>
      {sub && <div className="text-xs text-content-subtle">{sub}</div>}
    </div>
  );
}

function KpiBar({ totals, lostMoneyCents }: { totals: PerfTotals; lostMoneyCents: number }) {
  return (
    <div className="mb-4 flex flex-wrap divide-x divide-border rounded-md border border-border bg-surface-muted/40">
      <Kpi label="Clicks" value={fmtNum(totals.clicks)}
        sub={`${fmtNum(totals.uniqClicks)} unique`}
        delta={{ value: totals.clicks, base: totals.baseClicks }} />
      <Kpi label="New users" value={fmtNum(totals.fbNewUsers)}
        sub={totals.arrival == null ? 'no Firebase data' : `${fmtPct(totals.arrival, 0)} of clicks`}
        delta={{ value: totals.fbNewUsers, base: totals.baseFbNewUsers }} />
      <Kpi label="Regs" value={fmtNum(totals.regs)}
        sub={`${fmtPct(totals.regRate, 1)} of clicks`}
        delta={{ value: totals.regs, base: totals.baseRegs }} />
      <Kpi label="Deps" value={fmtNum(totals.deps)}
        sub={`${fmtPct(totals.depRate, 1)} of regs`}
        delta={{ value: totals.deps, base: totals.baseDeps }} />
      <Kpi label="Payout" value={fmtCents(totals.payoutCents)}
        sub={totals.epcCents == null ? undefined : `EPC ${fmtCents(totals.epcCents)}`}
        delta={{ value: totals.payoutCents, base: totals.basePayoutCents }} />
      <Kpi label="Estimated loss" value={fmtCents(lostMoneyCents)}
        sub={`${fmtNum(totals.lostRegs)} regs · ${totals.issues} flagged geo(s)`} />
    </div>
  );
}

/** The full case file for a flagged geo: symptom, bar, what was ruled out. */
function WhyFlagged({ explanation }: { explanation: PerfExplanation }) {
  return (
    <div className="mb-5 rounded-md border border-border bg-surface p-3">
      <h4 className="mb-1 text-sm font-medium text-content">Почему помечено</h4>
      <p className="mb-2 text-sm text-content">{explanation.symptom}</p>
      <dl className="grid gap-x-4 gap-y-1 text-xs text-content-muted sm:grid-cols-[auto,1fr]">
        {explanation.comparison.length > 0 && <>
          <dt className="text-content-subtle">Сравнение</dt>
          <dd>{explanation.comparison.join(' ')}</dd>
        </>}
        {explanation.ruledOut.length > 0 && <>
          <dt className="text-content-subtle">Исключено</dt>
          <dd>{explanation.ruledOut.join('; ')}.</dd>
        </>}
        {explanation.price && <>
          <dt className="text-content-subtle">Цена</dt>
          <dd>{explanation.price}</dd>
        </>}
        {explanation.checks.length > 0 && <>
          <dt className="text-content-subtle">Проверить</dt>
          <dd>
            <ul className="list-disc pl-4">
              {explanation.checks.map((c) => <li key={c}>{c}</li>)}
            </ul>
          </dd>
        </>}
        {explanation.caveat && <>
          <dt className="text-content-subtle">Оговорка</dt>
          <dd className="text-amber-700 dark:text-amber-400">{explanation.caveat}</dd>
        </>}
      </dl>
    </div>
  );
}

function RowDetail({ row, from, to, colSpan, thresholds }: {
  row: PerfRow; from: string; to: string; colSpan: number; thresholds: PerfThresholds | null;
}) {
  const c = useThemeColors();
  const explanation = thresholds ? explainRow(row, thresholds) : null;
  const q = { from, to, appId: row.appId, country: row.country };
  const { data: campaigns = [], isLoading: campLoading } = useQuery({
    queryKey: ['perf-campaigns', q],
    queryFn: () => getPerfCampaigns(q),
  });
  const { data: series = [], isLoading: seriesLoading } = useQuery({
    queryKey: ['perf-series', q],
    queryFn: () => getPerfSeries(q),
  });

  return (
    <tr className="bg-surface-muted/40">
      <td colSpan={colSpan} className="px-3 py-4">
        {explanation && <WhyFlagged explanation={explanation} />}
        <div className="grid gap-6 lg:grid-cols-2">
          <div>
            <h4 className="mb-2 text-sm font-medium text-content">By day</h4>
            {seriesLoading ? (
              <div className="flex h-48 items-center justify-center text-sm text-content-subtle">Loading…</div>
            ) : series.length === 0 ? (
              <div className="flex h-48 items-center justify-center text-sm text-content-subtle">No days with data</div>
            ) : (
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={series} margin={{ top: 5, right: 8, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={c.grid} />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke={c.axis} tickFormatter={(d) => formatChartDay(d)} />
                    <YAxis tick={{ fontSize: 11 }} stroke={c.axis} width={44} />
                    <Tooltip labelFormatter={(d) => formatChartDay(String(d), { month: 'short', day: 'numeric' })} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="clicks" name="Clicks" stroke={c.line} strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="fb_new_users" name="New users" stroke="#f59e0b" strokeWidth={2} dot={false} connectNulls={false} />
                    <Line type="monotone" dataKey="regs" name="Regs" stroke="#10b981" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="deps" name="Deps" stroke="#8b5cf6" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div>
            <h4 className="mb-2 text-sm font-medium text-content">
              By tracker campaign
              <span className="ml-2 text-xs font-normal text-content-subtle">Firebase cannot be split per campaign</span>
            </h4>
            {campLoading ? (
              <div className="py-6 text-center text-sm text-content-subtle">Loading…</div>
            ) : campaigns.length === 0 ? (
              <div className="py-6 text-center text-sm text-content-subtle">No campaign clicks in this period</div>
            ) : (
              <table className="min-w-full divide-y divide-border text-sm">
                <thead>
                  <tr>
                    <th className="px-2 py-1 text-left text-xs font-medium uppercase text-content-subtle">Campaign</th>
                    <th className="px-2 py-1 text-right text-xs font-medium uppercase text-content-subtle">Clicks</th>
                    <th className="px-2 py-1 text-right text-xs font-medium uppercase text-content-subtle">Regs</th>
                    <th className="px-2 py-1 text-right text-xs font-medium uppercase text-content-subtle">Reg%</th>
                    <th className="px-2 py-1 text-right text-xs font-medium uppercase text-content-subtle">Deps</th>
                    <th className="px-2 py-1 text-right text-xs font-medium uppercase text-content-subtle">Payout</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {campaigns.map((r) => (
                    <tr key={r.campaign_id}>
                      <td className="px-2 py-1">
                        <div className="text-content">{r.campaign_name || <span className="italic text-content-subtle">(deleted)</span>}</div>
                        {r.label && <div className="text-xs text-content-subtle">{r.label}</div>}
                      </td>
                      <td className="px-2 py-1 text-right">{fmtNum(r.clicks)}</td>
                      <td className="px-2 py-1 text-right">{fmtNum(r.regs)}</td>
                      <td className="px-2 py-1 text-right text-content-muted">{r.clicks ? fmtPct(r.regs / r.clicks) : '—'}</td>
                      <td className="px-2 py-1 text-right">{fmtNum(r.deps)}</td>
                      <td className="px-2 py-1 text-right">{fmtCents(r.payout_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
}

const TH = 'px-3 py-2 text-xs font-medium uppercase text-content-subtle';
const GROUP_HEAD = 'px-3 py-1 text-center text-[10px] font-semibold uppercase tracking-wider text-content-subtle';

/**
 * One table per traffic kind. `withFirebase` also decides whether the row can
 * carry a verdict at all: both need an app behind the traffic.
 */
function GroupTable({
  groups, withFirebase, from, to, isLoading, emptyText, thresholds = null,
}: {
  groups: PerfGroup[]; withFirebase: boolean; from: string; to: string;
  isLoading: boolean; emptyText: string; thresholds?: PerfThresholds | null;
}) {
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [showAll, setShowAll] = useState<Record<string, boolean>>({});
  const [openGeo, setOpenGeo] = useState<string | null>(null);

  const colSpan = withFirebase ? 13 : 9;
  const toggleGroup = (k: string) => setOpenGroups((s) => ({ ...s, [k]: !isOpen(k) }));
  // A group with something wrong opens on its own — that is the whole point of
  // the section — while healthy apps stay one line each.
  const isOpen = (k: string) => openGroups[k] ?? (groups.find((g) => g.key === k)?.issues ?? 0) > 0;

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-border">
        <thead className="bg-surface-muted">
          <tr>
            <th rowSpan={2} className={`${TH} text-left`}>{withFirebase ? 'App' : 'Campaign'} / Geo</th>
            <th colSpan={2} className={GROUP_HEAD}>Traffic</th>
            {withFirebase && <th colSpan={2} className={`${GROUP_HEAD} border-l border-border`}>Delivery</th>}
            <th colSpan={2} className={`${GROUP_HEAD} border-l border-border`}>Conversion</th>
            <th colSpan={4} className={`${GROUP_HEAD} border-l border-border`}>Money</th>
            {withFirebase && <th colSpan={2} className={`${GROUP_HEAD} border-l border-border`}>Result</th>}
          </tr>
          <tr>
            <th className={`${TH} text-right`}>Clicks</th>
            <th className={`${TH} text-right`} title="Unique clicks">Uniq</th>
            {withFirebase && <>
              <th className={`${TH} border-l border-border text-right`} title="Firebase new users in this geo, organic included">New users</th>
              <th className={`${TH} text-right`} title="New users / clicks">Arrival</th>
            </>}
            <th className={`${TH} border-l border-border text-right`}>Regs</th>
            <th className={`${TH} text-right`} title="Regs / clicks, with the change against the baseline">Reg%</th>
            <th className={`${TH} border-l border-border text-right`}>Deps</th>
            <th className={`${TH} text-right`} title="Deps / regs">Dep%</th>
            <th className={`${TH} text-right`}>Payout</th>
            <th className={`${TH} text-right`} title="Payout per click">EPC</th>
            {withFirebase && <>
              <th className={`${TH} border-l border-border text-right`} title="Regs missed against the reachable bar, priced at the app's payout per reg">Loss</th>
              <th className={`${TH} text-left`}>Verdict</th>
            </>}
          </tr>
        </thead>
        <tbody className="divide-y divide-border text-sm">
          {isLoading && <tr><td colSpan={colSpan} className="py-6 text-center text-content-subtle">Loading…</td></tr>}
          {!isLoading && groups.length === 0 && (
            <tr><td colSpan={colSpan} className="py-6 text-center text-content-subtle">{emptyText}</td></tr>
          )}
          {groups.map((g) => {
            const open = isOpen(g.key);
            const geos = showAll[g.key] ? [...g.geos, ...g.hidden] : g.geos;
            return (
              <Fragment key={g.key}>
                <tr
                  onClick={() => toggleGroup(g.key)}
                  className="cursor-pointer bg-surface-muted/30 hover:bg-surface-muted/60"
                >
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-content-subtle">{open ? '▾' : '▸'}</span>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-content">{g.name}</span>
                          {g.isBanned && (
                            <span
                              className="rounded bg-danger-soft px-1.5 py-0.5 text-[10px] font-medium text-danger"
                              title="Removed from store"
                            >Banned</span>
                          )}
                        </div>
                        <div className="text-xs text-content-subtle">
                          {g.subtitle}
                          {g.crashFreePct != null && <> · crash-free {g.crashFreePct.toFixed(1)}%</>}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right font-medium">
                    {fmtNum(g.clicks)}
                    <DeltaBadge value={g.clicks} base={g.baseClicks} />
                  </td>
                  <td className="px-3 py-2 text-right text-content-muted">{fmtNum(g.uniqClicks)}</td>
                  {withFirebase && <>
                    <td className="px-3 py-2 text-right">
                      {g.hasFirebase ? fmtNum(g.fbNewUsers) : <span className="text-xs text-content-subtle" title="No Firebase account linked to this app">no FB</span>}
                      <DeltaBadge value={g.fbNewUsers} base={g.baseFbNewUsers} />
                    </td>
                    <td className="px-3 py-2 text-right">{fmtPct(g.arrival, 0)}</td>
                  </>}
                  <td className="px-3 py-2 text-right font-medium">{fmtNum(g.regs)}</td>
                  <td className="px-3 py-2 text-right">
                    {fmtPct(g.regRate, 2)}
                    <DeltaBadge value={g.regRate} base={g.baseRegRate} />
                  </td>
                  <td className="px-3 py-2 text-right font-medium">{fmtNum(g.deps)}</td>
                  <td className="px-3 py-2 text-right text-content-muted">{fmtPct(g.depRate, 1)}</td>
                  <td className="px-3 py-2 text-right font-medium">
                    {fmtCents(g.payoutCents)}
                    <DeltaBadge value={g.payoutCents} base={g.basePayoutCents} />
                  </td>
                  <td className="px-3 py-2 text-right text-content-muted">{g.epcCents == null ? '—' : fmtCents(g.epcCents)}</td>
                  {withFirebase && <>
                    <td className="px-3 py-2 text-right">
                      {g.lostMoneyCents > 0 ? <span className="font-medium text-content">{fmtCents(g.lostMoneyCents)}</span> : '—'}
                    </td>
                    <td className="px-3 py-2">
                      {g.issues > 0
                        ? <span className="whitespace-nowrap text-amber-700 dark:text-amber-400">⚠ {g.issues} geo(s)</span>
                        : <span className="text-xs text-content-subtle">ok</span>}
                    </td>
                  </>}
                </tr>

                {open && geos.map((geo) => (
                  <GeoRow
                    key={geo.key}
                    geo={geo}
                    withFirebase={withFirebase}
                    expanded={openGeo === geo.key}
                    onToggle={() => geo.row && setOpenGeo(openGeo === geo.key ? null : geo.key)}
                    from={from}
                    to={to}
                    colSpan={colSpan}
                    thresholds={thresholds}
                  />
                ))}

                {open && g.hiddenGeos > 0 && (
                  <tr>
                    <td colSpan={colSpan} className="px-3 py-1 pl-10">
                      <button
                        type="button"
                        onClick={() => setShowAll((s) => ({ ...s, [g.key]: !s[g.key] }))}
                        className="text-xs text-content-subtle underline"
                      >
                        {showAll[g.key]
                          ? 'hide small geos'
                          : `…${g.hiddenGeos} more geo(s) below the noise floor · ${fmtNum(g.hiddenClicks)} clicks`}
                      </button>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function GeoRow({ geo, withFirebase, expanded, onToggle, from, to, colSpan, thresholds }: {
  geo: GeoLine; withFirebase: boolean; expanded: boolean; onToggle: () => void;
  from: string; to: string; colSpan: number; thresholds: PerfThresholds | null;
}) {
  const v = VERDICTS[geo.verdict];
  // Visible without hovering: the badge says what we concluded, this says why.
  const explanation = geo.row && thresholds ? explainRow(geo.row, thresholds) : null;
  return (
    <>
      <tr
        onClick={onToggle}
        className={`${geo.row ? 'cursor-pointer' : ''} hover:bg-surface-muted/60 ${geo.noisy ? 'text-content-subtle' : ''}`}
      >
        <td className="px-3 py-1.5 pl-10">
          <span className="font-medium">{geo.country}</span>
          <span className="ml-1 text-xs text-content-subtle">{countryLabel(geo.country)}</span>
        </td>
        <td className="px-3 py-1.5 text-right">{fmtNum(geo.clicks)}</td>
        <td className="px-3 py-1.5 text-right text-content-muted">{fmtNum(geo.uniqClicks)}</td>
        {withFirebase && <>
          <td className="px-3 py-1.5 text-right">
            {fmtNum(geo.fbNewUsers)}
            <DeltaBadge value={geo.fbNewUsers} base={geo.baseFbNewUsers} muted={geo.noisy} />
          </td>
          <td className="px-3 py-1.5 text-right">{fmtPct(geo.arrival, 0)}</td>
        </>}
        <td className="px-3 py-1.5 text-right">{fmtNum(geo.regs)}</td>
        <td className="px-3 py-1.5 text-right">
          {fmtPct(geo.regRate, 2)}
          <DeltaBadge value={geo.regRate} base={geo.baseRegRate} muted={geo.noisy} />
        </td>
        <td className="px-3 py-1.5 text-right">{fmtNum(geo.deps)}</td>
        <td className="px-3 py-1.5 text-right text-content-muted">{fmtPct(geo.depRate, 1)}</td>
        <td className="px-3 py-1.5 text-right">{fmtCents(geo.payoutCents)}</td>
        <td className="px-3 py-1.5 text-right text-content-muted">{geo.epcCents == null ? '—' : fmtCents(geo.epcCents)}</td>
        {withFirebase && <>
          <td className="px-3 py-1.5 text-right">
            {geo.lostRegs > 0 ? (
              <span title={`${geo.lostRegs} regs below the reachable bar`}>
                <span className="font-medium text-content">{fmtCents(geo.lostMoneyCents)}</span>
                <span className="ml-1 text-xs text-content-subtle">{geo.lostRegs} regs</span>
              </span>
            ) : '—'}
          </td>
          <td className="px-3 py-1.5">
            {geo.verdict === 'none' ? (
              <span className="text-xs text-content-subtle">{geo.noisy ? 'too few clicks' : '—'}</span>
            ) : (
              <span title={v.hint} className="whitespace-nowrap">
                {v.icon} {v.label}
                {geo.fbMissing && <span className="ml-1 text-xs text-content-subtle" title="No Firebase data — verdict rests on tracker numbers alone">?</span>}
              </span>
            )}
          </td>
        </>}
      </tr>
      {explanation && !expanded && (
        <tr onClick={onToggle} className="cursor-pointer hover:bg-surface-muted/60">
          <td colSpan={colSpan} className="px-3 pb-2 pl-10 pt-0">
            <div className="text-xs text-content-muted">
              <span className="mr-1">{v.icon || '•'}</span>
              {explanation.symptom}
              <span className="ml-1 whitespace-nowrap text-content-subtle underline">разбор</span>
            </div>
          </td>
        </tr>
      )}
      {expanded && geo.row && <RowDetail row={geo.row} from={from} to={to} colSpan={colSpan} thresholds={thresholds} />}
    </>
  );
}

export function Performance() {
  const today = Date.now();
  const [from, setFrom] = useState(dateStr(today - 7 * DAY_MS));
  const [to, setTo] = useState(dateStr(today - DAY_MS));
  const [appId, setAppId] = useState('');
  const [country, setCountry] = useState('');
  const [issuesOnly, setIssuesOnly] = useState(true);
  // Banned apps still hold history the report cannot act on, so they are out of
  // the way by default — with a visible count, never silently dropped.
  const [hideBanned, setHideBanned] = useState(true);

  const { data, isLoading, error } = useQuery({
    queryKey: ['perf-rows', from, to, appId],
    queryFn: () => getPerfRows({ from, to, appId: appId || undefined }),
  });
  const { data: noAppRows = [], isLoading: noAppLoading } = useQuery({
    queryKey: ['perf-no-app', from, to],
    queryFn: () => getNoAppPerfRows({ from, to }),
    // Filtering by one app is a question about apps; the PWA section has none.
    enabled: !appId,
  });

  const allRows = data?.rows ?? [];
  const t = data?.thresholds;
  const minClicks = t?.minClicks ?? 30;

  // Filter options come from the response itself, so the page needs no extra
  // grants (the apps list lives behind its own section).
  const appOptions = useMemo<SelectOption[]>(() => {
    const m = new Map<string, string>();
    allRows.forEach((r) => m.set(r.appId, r.appName));
    return [
      { value: '', label: 'All apps' },
      ...Array.from(m, ([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label)),
    ];
  }, [allRows]);

  const countryOptions = useMemo<SelectOption[]>(() => {
    const set = new Set(allRows.map((r) => r.country));
    noAppRows.forEach((r) => set.add(r.country));
    return [
      { value: '', label: 'All geos' },
      ...Array.from(set)
        .map((code) => ({ value: code, label: `${code} · ${countryLabel(code)}` }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ];
  }, [allRows, noAppRows]);

  const appGroups = useMemo(
    () => groupAppRows(country ? allRows.filter((r) => r.country === country) : allRows,
      { minClicks, issuesOnly, hideBanned }),
    [allRows, country, minClicks, issuesOnly, hideBanned],
  );
  const noAppGroups = useMemo(
    () => groupNoAppRows(country ? noAppRows.filter((r) => r.country === country) : noAppRows,
      { minClicks, issuesOnly: false }),
    [noAppRows, country, minClicks],
  );

  // The KPI bar answers "how did the period go", so it counts every app row —
  // the section below it is what the issues filter narrows.
  const totals = useMemo(
    () => sumGroups(groupAppRows(country ? allRows.filter((r) => r.country === country) : allRows,
      { minClicks, issuesOnly: false, hideBanned })),
    [allRows, country, minClicks, hideBanned],
  );

  const bannedApps = useMemo(
    () => new Set(allRows.filter((r) => r.isBanned).map((r) => r.appId)).size,
    [allRows],
  );
  const noAppTotals = useMemo(() => sumGroups(noAppGroups), [noAppGroups]);

  const setPreset = (days: number) => {
    // Ranges end yesterday: today is still filling up and GA4 backfills it.
    setTo(dateStr(today - DAY_MS));
    setFrom(dateStr(today - days * DAY_MS));
  };

  return (
    <div>
      <div className="mb-4 flex flex-col justify-between gap-3 sm:mb-6 sm:flex-row sm:items-start">
        <div>
          <h1 className="text-xl font-semibold text-content sm:text-2xl">Performance</h1>
          <p className="mt-1 max-w-2xl text-sm text-content-subtle">
            The tracker funnel per app and geo, next to Firebase arrivals. A verdict is only issued when
            registrations or deposits fall below the reachable bar; arrivals then explain why.
            New users count first opens in that geo (organic included, already-installed users and
            browser offers excluded), so the column is context, not a funnel step.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="inline-flex overflow-hidden rounded-md border border-border text-sm">
            {[
              { label: 'Yesterday', days: 1 },
              { label: '7d', days: 7 },
              { label: '30d', days: 30 },
            ].map((p, i) => (
              <button
                key={p.label}
                type="button"
                onClick={() => setPreset(p.days)}
                className={`px-3 py-1.5 ${i > 0 ? 'border-l border-border' : ''} bg-surface text-content-muted hover:bg-surface-muted`}
              >{p.label}</button>
            ))}
          </div>
          <div>
            <label className="mb-1 block text-xs text-content-subtle">From</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-md border border-border px-3 py-1.5 text-sm" />
          </div>
          <div>
            <label className="mb-1 block text-xs text-content-subtle">To</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-md border border-border px-3 py-1.5 text-sm" />
          </div>
          <div className="min-w-[200px]">
            <label className="mb-1 block text-xs text-content-subtle">App</label>
            <Select value={appId} options={appOptions} onChange={setAppId} searchable size="sm" placeholder="All apps" />
          </div>
          <div className="min-w-[180px]">
            <label className="mb-1 block text-xs text-content-subtle">GEO</label>
            <Select value={country} options={countryOptions} onChange={setCountry} searchable size="sm" placeholder="All geos" />
          </div>
          <div className="flex flex-col gap-1 pb-1.5 text-sm text-content-muted">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={issuesOnly} onChange={(e) => setIssuesOnly(e.target.checked)} />
              Problems only
            </label>
            <label className="flex items-center gap-2" title="Apps removed from the store keep their history, but nothing here can be acted on">
              <input type="checkbox" checked={hideBanned} onChange={(e) => setHideBanned(e.target.checked)} />
              Hide banned
            </label>
          </div>
        </div>
      </div>

      <KpiBar totals={totals} lostMoneyCents={totals.lostMoneyCents} />

      <div className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-content-subtle">
        {data && <span>Baseline: {data.baseline_from} → {data.baseline_to} (same length, immediately before)</span>}
        {t && <span>Geos under {t.minClicks} clicks are never judged and fold away</span>}
        {bannedApps > 0 && (
          <span>
            {hideBanned
              ? `${bannedApps} banned app(s) hidden — uncheck “Hide banned” to include them`
              : `${bannedApps} banned app(s) included`}
          </span>
        )}
      </div>

      {data?.fb_health && <FbHealthNotice health={data.fb_health} />}

      {error && (
        <div className="mb-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
          {(error as Error).message}
        </div>
      )}

      <h2 className="mb-2 flex items-baseline gap-2 text-sm font-semibold uppercase tracking-wide text-content-muted">
        Apps
        <span className="text-xs font-normal normal-case text-content-subtle">
          {totals.groups} app(s) · {fmtNum(totals.clicks)} clicks
        </span>
      </h2>
      <GroupTable
        groups={appGroups}
        withFirebase
        from={from}
        to={to}
        isLoading={isLoading}
        emptyText={issuesOnly ? 'No problems detected in this period' : 'No data for this period'}
        thresholds={t ?? null}
      />

      {!appId && (
        <>
          <h2 className="mb-2 mt-8 flex items-baseline gap-2 text-sm font-semibold uppercase tracking-wide text-content-muted">
            No app behind it
            <span className="text-xs font-normal normal-case text-content-subtle">
              PWA and landing campaigns · {fmtNum(noAppTotals.clicks)} clicks · {fmtNum(noAppTotals.regs)} regs ·{' '}
              {fmtNum(noAppTotals.deps)} deps — no Firebase, so no arrivals and no verdicts
            </span>
          </h2>
          <GroupTable
            groups={noAppGroups}
            withFirebase={false}
            from={from}
            to={to}
            isLoading={noAppLoading}
            emptyText="No app-less tracker traffic in this period"
          />
        </>
      )}
    </div>
  );
}
